import {
    Component,
    EventEmitter,
    NgZone,
    OnDestroy,
    Output,
} from '@angular/core';
import Player from '@vimeo/player';
import { from, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, take, tap } from 'rxjs/operators';

import { PlaylistItem } from './vimeo.model';
import { VimeoService } from './vimeo.service';

declare let Zone;

export interface PlayerEvent {
    event: string;
    data: any;
    playlistItem?: PlaylistItem;
}

class InternalPlayer {
    private _current: PlaylistItem;
    private _player: Player;

    private readonly _eventSubject: Subject<PlayerEvent>;
    event: Observable<PlayerEvent>;

    constructor(item?: any) {
        this._current = item;
        this._eventSubject = new Subject();
        this.event = this._eventSubject.asObservable();
    }

    private _track(event: string, data: any): void {
        // TODO revise. no playlistItem object reference for track events
        this._eventSubject.next({
            event: 'track',
            data: {
                ...data,
                event,
                playlistVimeoId: this._current.playlistVimeoId,
                playlistItemVimeoId: this._current.vimeoId,
            },
        });
    }

    init(): void {
        if (!this._current) return;
        this.destroy().subscribe(() => {
            this._player = new Player('cmpsVimeoPlayerContainer', {
                id: this._current.vimeoId,
            });
            Zone.root.run(() => {
                ['play', 'pause', 'ended', 'seeked', 'loaded'].forEach(
                    (event) => {
                        this._player.on(event, (data) => {
                            this._track(event, data);
                            this._eventSubject.next({
                                event,
                                data,
                                playlistItem: this._current,
                            });
                        });
                    },
                );
            });
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    destroy(): Observable<void | {}> {
        if (!this._player) return of(null);
        return from(this._player.destroy()).pipe(
            catchError(() => null),
            tap(() => {
                if (this._eventSubject) {
                    this._eventSubject.complete();
                }
            }),
        );
    }

    loadVideo(item: any): Observable<number | {}> {
        this._current = item;
        if (!this._player) {
            this.init();
            return of(null);
        }
        return from(this._player.unload()).pipe(
            catchError(() => null),
            concatMap(() =>
                from(this._player.loadVideo(this._current.vimeoId)),
            ),
        );
    }

    play(): Observable<void> {
        return from(this._player.play());
    }
}

@Component({
    selector: 'cmps-vimeo-player',
    templateUrl: './player.vimeo.component.html',
    exportAs: 'cmpsVimeoPlayer',
})
export class PlayerVimeoComponent implements OnDestroy {
    @Output() event: EventEmitter<PlayerEvent>;

    private readonly _player: InternalPlayer;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _service: VimeoService,
    ) {
        this.event = new EventEmitter();
        this._player = new InternalPlayer();
        this._player.event.subscribe((event) => {
            this._ngZone.run(() => {
                if (event.event === 'track') {
                    this._service
                        .createTrackVideoEvent({
                            name: event.data.event,
                            playlistVimeoId: event.data.playlistVimeoId,
                            playlistItemVimeoId: event.data.playlistItemVimeoId,
                            position: event.data.seconds,
                            timestamp: new Date(),
                            userId: null,
                        })
                        .pipe(take(1))
                        .subscribe(null, () => null);
                } else {
                    if (event.event === 'play') {
                        event.playlistItem.lastWatched = {
                            playlistId: event.playlistItem.playlistId,
                            playlistItemId: event.playlistItem.playlistItemId,
                            timestamp: new Date(),
                        };
                    }
                    this.event.emit(event);
                }
            });
        });
    }

    ngOnDestroy(): void {
        this._player.destroy().subscribe(null);
    }

    loadVideo(item: any): Observable<number | {}> {
        return this._player.loadVideo(item);
    }

    play(): Observable<void> | undefined {
        return this._player.play();
    }
}
